import React from "react";
import { Scale } from "lucide-react";
import LegalHero from "@/components/legal/LegalHero";
import TableOfContents from "@/components/legal/TableOfContents";
import PolicySection from "@/components/legal/PolicySection";
import BackToTop from "@/components/legal/BackToTop";
import ContactInfo from "@/components/legal/ContactInfo";
import useSEO from "@/hooks/useSEO";
import { STATIC_ROUTE_META } from "@/utils/routeMetadata";

const Terms = () => {
  const formattedDate = "October 11, 2026";
  
  useSEO(STATIC_ROUTE_META["/terms"]);
  
  // Table of contents items
  const tocItems = [
    { id: "acceptance", title: "Acceptance of Terms", icon: <Scale size={18} /> },
    { id: "description", title: "Description of the Service", icon: <Scale size={18} /> },
    { id: "ip", title: "Intellectual Property", icon: <Scale size={18} /> },
    { id: "conduct", title: "User Conduct", icon: <Scale size={18} /> },
    { id: "disclaimers", title: "Disclaimers", icon: <Scale size={18} /> },
    { id: "liability", title: "Limitation of Liability", icon: <Scale size={18} /> },
    { id: "indemnification", title: "Indemnification", icon: <Scale size={18} /> },
    { id: "modifications", title: "Modifications and Termination", icon: <Scale size={18} /> },
    { id: "governing", title: "Governing Law", icon: <Scale size={18} /> },
    { id: "contact", title: "Contact Information", icon: <Scale size={18} /> },
  ];
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <LegalHero 
        title="Terms & Conditions"
        subtitle="The rules and guidelines for using our services"
        icon={<Scale size={32} className="text-elfGreen" />}
        date={formattedDate}
      />

      {/* Table of Contents */}
      <TableOfContents items={tocItems} />

      {/* Content Sections */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto prose prose-lg">
            <PolicySection id="acceptance" title="Acceptance of Terms" icon={<Scale size={20} />} number={1}>
              <p>
                By accessing or using the Elf Letter Bot website ("the Site"), you agree to be bound by these Terms & Conditions and all applicable laws. If you do not agree, please do not access or use the Site.
              </p>
            </PolicySection>
            
            <PolicySection id="description" title="Description of the Service" icon={<Scale size={20} />} number={2}>
              <p>
                Elf Letter Bot provides a free elf letter generator tool and related blog articles, tips, and other content about holiday elf traditions and family activities. The Site also includes call-to-action links to external platforms like The Magic Letter Box, which operate independently.
              </p>
              <p>
                To protect the free tier and prevent abuse, we currently limit free elf letter deliveries to three (3) emailed letters per email address. We may store hashed versions of email addresses in order to enforce this limit. Additional letters and enhanced features may be available through external services such as The Magic Letter Box.
              </p>
              <p>
                By requesting your free letter, you’ll also receive occasional holiday emails from The Magic Letter Box. Unsubscribe anytime using the link in your delivery email. We honor existing unsubscribe requests; requesting a PDF does not cancel them.
              </p>
            </PolicySection>
            
            <PolicySection id="ip" title="Intellectual Property" icon={<Scale size={20} />} number={3}>
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-elfRed mb-3">Site Content:</h3>
                <p>
                  All content, features, and functionality on the Site (such as text, graphics, logos, images, and digital downloads) are owned by or licensed to us and are protected by copyright and other intellectual property laws.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-elfRed mb-3">Trademarks:</h3>
                <p>
                  All trademarks, service marks, and trade names are the property of their respective owners. Elf Letter Bot is not affiliated with or endorsed by any proprietary holiday elf brands or characters.
                </p>
              </div>
            </PolicySection>
            
            <PolicySection id="conduct" title="User Conduct" icon={<Scale size={20} />} number={4}>
              <p>By using this Site, you agree to:</p>
              <ul className="space-y-2 mt-3">
                <li className="flex items-start gap-2">
                  <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
                  <span>Use the Site in accordance with these Terms & Conditions and any applicable laws or regulations.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
                  <span>Not engage in any activity that disrupts or interferes with the Site's functionality or security.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
                  <span>Not use the Site for any unlawful or fraudulent purpose.</span>
                </li>
              </ul>
            </PolicySection>
            
            <PolicySection id="disclaimers" title="Disclaimers" icon={<Scale size={20} />} number={5}>
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-elfRed mb-3">Content Accuracy:</h3>
                <p>
                  While we make reasonable efforts to provide accurate and up-to-date information, the Site content is provided on an "AS IS" basis, without warranties of any kind. We do not warrant the accuracy, completeness, or usefulness of the information provided.
                </p>
              </div>
              
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-elfRed mb-3">Not Legal or Professional Advice:</h3>
                <p>
                  All information provided on the Site is for informational purposes only and does not constitute legal, health, or other professional advice.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-elfRed mb-3">External Links:</h3>
                <p>
                  The Site may contain links to third-party websites or services, including Magic Letter Box. We are not responsible for the content or services of these third-party sites.
                </p>
              </div>
            </PolicySection>
            
            <PolicySection id="liability" title="Limitation of Liability" icon={<Scale size={20} />} number={6}>
              <p>
                To the fullest extent permitted by law, Elf Letter Bot and its affiliates, employees, agents, or licensors shall not be liable for any indirect, incidental, special, consequential, or punitive damages (including but not limited to loss of profits, data, or other intangibles) arising out of or in connection with your use of the Site or any content provided on the Site.
              </p>
            </PolicySection>
            
            <PolicySection id="indemnification" title="Indemnification" icon={<Scale size={20} />} number={7}>
              <p>
                You agree to defend, indemnify, and hold us harmless from and against any claims, damages, costs, liabilities, and expenses (including reasonable attorneys' fees) arising out of or related to your use of the Site, violation of these Terms & Conditions, or infringement of any intellectual property or other rights of any person or entity.
              </p>
            </PolicySection>
            
            <PolicySection id="modifications" title="Modifications and Termination" icon={<Scale size={20} />} number={8}>
              <p>
                We reserve the right to modify or discontinue any part of the Site, temporarily or permanently, with or without notice. We may also revise these Terms & Conditions at any time by updating this page. Your continued use of the Site after any such changes constitutes your acceptance of the new Terms & Conditions.
              </p>
            </PolicySection>
            
            <PolicySection id="governing" title="Governing Law" icon={<Scale size={20} />} number={9}>
              <p>
                These Terms & Conditions and any disputes arising out of or related to these Terms & Conditions or the use of the Site shall be governed by and construed in accordance with the laws of Minnesota, USA, without regard to its conflict of law principles.
              </p>
            </PolicySection>
            
            <PolicySection id="contact" title="Contact Information" icon={<Scale size={20} />} number={10}>
              <p>
                If you have any questions or concerns about these Terms & Conditions, please contact us at:
              </p>
              <ContactInfo email="support@elfletterbot.com" />
            </PolicySection>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <BackToTop />
    </div>
  );
};

export default Terms;
